// SYSTEM_ROUTE: /direction
import { useState } from 'react';
import type { Goal, GoalHorizon, GoalProgress, GoalRollup, Milestone, Pillar } from '@life-os/app-core';
import {
  GOAL_HORIZONS,
  MILESTONE_STATUSES,
  PILLAR_LABEL,
  PILLARS,
  goalProgress,
  summarizeDirection,
} from '@life-os/app-core';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

const MS_TONE: Record<Milestone['status'], ChipTone> = {
  open: 'neutral',
  in_progress: 'warn',
  done: 'primary',
};

const HEALTH_TONE: Record<GoalRollup['health'], ChipTone> = {
  on_track: 'primary',
  at_risk: 'warn',
  blocked: 'danger',
  no_signal: 'muted',
};

const HEALTH_LABEL: Record<GoalRollup['health'], string> = {
  on_track: 'on track',
  at_risk: 'at risk',
  blocked: 'blocked',
  no_signal: 'no signal yet',
};

function ProgressBar({ fraction }: { fraction: number }) {
  return (
    <div style={{ height: 6, borderRadius: 999, background: 'var(--lc-surface-container-high, var(--lc-surface-container))', overflow: 'hidden', minWidth: 80, flex: 1 }}>
      <div style={{ width: `${Math.round(fraction * 100)}%`, height: '100%', background: 'var(--lc-primary)' }} />
    </div>
  );
}

interface Data {
  goals: readonly Goal[];
  milestones: readonly Milestone[];
  rollups: Record<string, GoalRollup>;
}

export function DirectionScreen() {
  const services = useServices();
  const { revision, refresh, data: ctx } = useStore();

  const [goalTitle, setGoalTitle] = useState('');
  const [pillar, setPillar] = useState<Pillar>('mission');
  const [horizon, setHorizon] = useState<GoalHorizon>('year');
  const [why, setWhy] = useState('');
  const [msTitle, setMsTitle] = useState('');

  const data = useAsync<Data>(async () => {
    const [goals, milestones] = await Promise.all([
      services.direction.listGoals(),
      services.direction.listMilestones(),
    ]);
    const active = goals.filter((g) => g.status === 'active');
    const rollupEntries = await Promise.all(
      active.map(async (g) => [String(g.id), await ctx.growth.goalRollup(g.id)] as const),
    );
    return { goals, milestones, rollups: Object.fromEntries(rollupEntries) };
  }, [revision]);

  const goals = data.value?.goals ?? [];
  const milestones = data.value?.milestones ?? [];
  const rollups = data.value?.rollups ?? {};
  const summary = summarizeDirection(goals);

  const addGoal = async () => {
    if (goalTitle.trim().length === 0) return;
    await services.direction.createGoal({ title: goalTitle, pillar, horizon, why });
    setGoalTitle('');
    setWhy('');
    refresh();
  };

  const addMilestone = async () => {
    if (msTitle.trim().length === 0) return;
    await services.direction.createMilestone({ title: msTitle });
    setMsTitle('');
    refresh();
  };

  const activeGoals = goals.filter((g) => g.status === 'active');

  return (
    <>
      <ScreenHeader title={<>Direction</>} subtitle={<>Vision becomes goals, goals become milestones, milestones feed targets and tasks. Breadth
            across pillars, no scoreboard — just what matters and the next move.</>} />

      <Pane title="Pillars" hint={`${summary.activeGoals} active goal${summary.activeGoals === 1 ? '' : 's'}`}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {PILLARS.map((p) => (
            <Chip key={p} tone={summary.byPillar[p] > 0 ? 'primary' : 'muted'}>
              {PILLAR_LABEL[p]} {summary.byPillar[p] > 0 ? summary.byPillar[p] : ''}
            </Chip>
          ))}
        </div>
        {summary.neglectedPillars.length > 0 ? (
          <div style={{ marginTop: 8, color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>
            No active goal in: {summary.neglectedPillars.map((p) => PILLAR_LABEL[p]).join(', ')}. Breadth is a
            choice, not a debt.
          </div>
        ) : null}
      </Pane>

      <Pane title="New goal" hint="What outcome, in which pillar, and why">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 200px' }}
            value={goalTitle}
            placeholder="Goal…"
            onChange={(e) => setGoalTitle(e.target.value)}
            aria-label="Goal title"
          />
          <select value={pillar} onChange={(e) => setPillar(e.target.value as Pillar)} aria-label="Pillar" style={inputStyle}>
            {PILLARS.map((p) => (
              <option key={p} value={p}>
                {PILLAR_LABEL[p]}
              </option>
            ))}
          </select>
          <select value={horizon} onChange={(e) => setHorizon(e.target.value as GoalHorizon)} aria-label="Horizon" style={inputStyle}>
            {GOAL_HORIZONS.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 200px' }}
            value={why}
            placeholder="Why it matters…"
            onChange={(e) => setWhy(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void addGoal();
            }}
            aria-label="Why"
          />
          <Button variant="primary" onClick={() => void addGoal()}>
            Add goal
          </Button>
        </div>
      </Pane>

      <Pane title="Goals" hint={`${activeGoals.length} active`}>
        {activeGoals.length === 0 ? (
          <EmptyState title="No goals yet">
            Name a goal in any pillar. Goals connect your daily targets and tasks to something that matters.
          </EmptyState>
        ) : (
          <Stack>
            {activeGoals.map((g) => {
              const progress: GoalProgress = goalProgress(g.id, milestones);
              const linked = milestones.filter((m) => m.contributesToGoalIds.some((id) => String(id) === String(g.id)));
              return (
                <div key={String(g.id)} className="lc-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap' }}>
                    <strong style={{ flex: 1 }}>{g.title}</strong>
                    {rollups[String(g.id)] ? (
                      <Chip tone={HEALTH_TONE[rollups[String(g.id)]!.health]} title={`Cross-domain signal: ${rollups[String(g.id)]!.score}/100`}>
                        {HEALTH_LABEL[rollups[String(g.id)]!.health]}
                        {rollups[String(g.id)]!.openBlockers > 0 ? ` · ${rollups[String(g.id)]!.openBlockers} blocker${rollups[String(g.id)]!.openBlockers === 1 ? '' : 's'}` : ''}
                      </Chip>
                    ) : null}
                    <Chip tone="neutral">{PILLAR_LABEL[g.pillar]}</Chip>
                    <Chip tone="muted">{g.horizon}</Chip>
                    <Button variant="ghost" onClick={() => void services.direction.setGoalStatus(g.id, 'achieved').then(refresh)}>
                      Achieved
                    </Button>
                  </div>
                  {g.why ? <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)' }}>Why: {g.why}</div> : null}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ProgressBar fraction={progress.fraction} />
                    <span style={{ fontSize: 'var(--lc-text-sm)', color: 'var(--lc-on-surface-variant)' }}>
                      {progress.doneMilestones}/{progress.totalMilestones}
                    </span>
                  </div>
                  {linked.length > 0 ? (
                    <Stack>
                      {linked.map((m) => (
                        <CompactRow
                          key={String(m.id)}
                          title={m.title}
                          meta={
                            <>
                              <Chip tone={MS_TONE[m.status]}>{m.status}</Chip>
                              {m.status !== 'done' ? (
                                <Button variant="ghost" onClick={() => void services.direction.setMilestoneStatus(m.id, 'done').then(refresh)}>
                                  Mark done
                                </Button>
                              ) : null}
                            </>
                          }
                        />
                      ))}
                    </Stack>
                  ) : null}
                </div>
              );
            })}
          </Stack>
        )}
      </Pane>

      <Pane title="Milestones" hint="A milestone can advance several goals">
        <Stack>
          <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)' }}>
            <input
              className="lc-cmdbar"
              style={{ flex: 1 }}
              value={msTitle}
              placeholder="Milestone…"
              onChange={(e) => setMsTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void addMilestone();
              }}
              aria-label="Milestone title"
            />
            <Button variant="primary" onClick={() => void addMilestone()}>
              Add
            </Button>
          </div>
          {milestones.length === 0 ? (
            <EmptyState title="No milestones yet">
              Add a milestone, then link it to one or more goals to show how progress contributes.
            </EmptyState>
          ) : (
            milestones.map((m) => (
              <div key={String(m.id)} className="lc-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lc-stack-sm)' }}>
                  <span style={{ flex: 1 }}>{m.title}</span>
                  <select
                    value={m.status}
                    onChange={(e) => void services.direction.setMilestoneStatus(m.id, e.target.value as Milestone['status']).then(refresh)}
                    aria-label="Milestone status"
                    style={inputStyle}
                  >
                    {MILESTONE_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                {activeGoals.length > 0 ? (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {activeGoals.map((g) => {
                      const linked = m.contributesToGoalIds.some((id) => String(id) === String(g.id));
                      return (
                        <button
                          key={String(g.id)}
                          type="button"
                          onClick={() => void services.direction.linkMilestoneToGoal(m.id, g.id).then(refresh)}
                          aria-pressed={linked}
                          style={{
                            ...inputStyle,
                            height: 26,
                            cursor: 'pointer',
                            background: linked ? 'var(--lc-primary)' : 'var(--lc-surface-container)',
                            color: linked ? 'var(--lc-on-primary)' : 'var(--lc-on-surface-variant)',
                          }}
                        >
                          {linked ? '✓ ' : '+ '}
                          {g.title}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ))
          )}
        </Stack>
      </Pane>
    </>
  );
}
